import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DataService } from '@manager-app/service/data.service';
import { Subscription } from 'rxjs';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';

import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumber,
  extractNumberArr,
  HxCurrencyPipe,
  HxStorageInvoiceService,
  HxStoreService,
  HxUiLabelPipe,
  OrderAction,
  StorageInvoiceModel,
  StorageInvoiceStatus,
  StoreBasicModel,
  toRouterQueryParams,
  uiLabel
} from 'hx-services';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { NgbModal, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { StorageInvoicePayModal } from '@manager-app/storage/storage-invoices-list/storage-invoice-pay-modal/storage-invoice-pay.modal';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  HxCheckboxComponent,
  HxDateRangeSelectComponent,
  HxEnumSelectComponent,
  HxLoaderComponent,
  HxProductInfoSelectComponent,
  HxStoreSelectComponent
} from 'hx-component';
import { FormsModule } from '@angular/forms';
import { SuppliersSearchComponent } from '../../components/suppliers-search/suppliers-search.component';

interface SelectableStorageInvoice extends StorageInvoiceModel {
  selected?: boolean;
}

@Component({
    selector: 'app-storage-invoices-list.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './storage-invoices-list.component.html',
    styleUrls: ['./storage-invoices-list.component.css'],
    imports: [NgClass, RouterLink, HxStoreSelectComponent, FormsModule, HxProductInfoSelectComponent, HxDateRangeSelectComponent, SuppliersSearchComponent, HxEnumSelectComponent, HxCheckboxComponent, NgIf, NgFor, HxLoaderComponent, NgbPagination, DatePipe, TranslocoPipe, HxCurrencyPipe, HxUiLabelPipe]
})
export class StorageInvoicesListComponent implements OnInit, OnDestroy {
  StorageInvoiceStatus = StorageInvoiceStatus;
  list: SelectableStorageInvoice[] = [];
  pagination = {allItemCount: 0};
  isLoading = {
    list: false,
    create: false,
    paid: false,
  };
  statusList = DataService.statusList;
  isAll = false;
  params: {
    page?: number;
    limit?: number;
    storeIds?: number[];
    query?: string;
    createDate?: DateRange;
    incomingDate?: DateRange;
    paidDate?: DateRange;
    productInfoIds?: number[];
    supplierId?: number;
    spendCategoryId?: number;
    paid?: boolean,
    statuses?: StorageInvoiceStatus[];
    dueDate?: DateRange;
  } = {page: 1, limit: 20};
  brandIds: number[] = [];
  private timeout: NodeJS.Timeout;
  private sub: Subscription;

  constructor(
    private storageInvoiceService: HxStorageInvoiceService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private errorService: ErrorHandlerService,
    private toastr: HcToastrService,
    private modal: NgbModal,
    private storeService: HxStoreService,
    private tr: TranslocoService,
  ) {
  }

  ngOnInit() {
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name)?.split(',').map(mapFn) ?? undefined;
      const asBooleanFn = (name: string) => paramMap.get(name) ? paramMap.get(name) === 'true' : undefined;
      this.params.createDate = extractDateRange('createDate', paramMap);
      this.params.incomingDate = extractDateRange('incomingDate', paramMap);
      this.params.paidDate = extractDateRange('paidDate', paramMap);
      this.params.supplierId = extractNumber('supplierId', paramMap);
      this.params.spendCategoryId = extractNumber('spendCategoryId', paramMap);
      this.params.paid = asBooleanFn('paid');
      this.params.productInfoIds = extractNumberArr('productInfoIds', paramMap);
      this.params.statuses = asArrFn('statuses', str => str as OrderAction) ?? [];
      this.params.query = paramMap.get('query');
      this.params.storeIds = asArrFn('storeIds', str => Number(str)) ?? extractDefaultStoreIds();
      this.params.dueDate = extractDateRange('dueDate', paramMap);
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      if (this.params.storeIds) {
        this.loadList();
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  applyFilters() {
    this.pageChanged(1);
  }

  pageChanged(page: number) {
    this.params.page = page;
    this.navigateUrl();
  }

  resetFilter() {
    this.pagination.allItemCount = 0;
    this.params = {page: 1, limit: 20};
    this.navigateUrl();
  }

  checkAll(): void {
    this.list.filter(r => !r.paymentDate).forEach(si => si.selected = this.isAll);
  }

  toggleStorageInvoice(si: SelectableStorageInvoice): void {
    si.selected = !si.selected;
  }

  paidConfirm() {
    const invoiceIds = this.list.filter(si => si.selected).map(si => si.id);
    const stores = this.list.filter(si => si.selected).map(si => si.store).filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
    if (invoiceIds.length > 0) {
      let notPermittedStores: string[] = [];
      this.storeService.getAvailableForPaymentApprovals().subscribe(result => {
        const availableStoreIds = result.map(r => r.id);
        stores.forEach(store => {
          if (!availableStoreIds.includes(store.id)) {
            notPermittedStores.push(uiLabel(this.tr.getActiveLang(), store.title));
          }
        });
        if (notPermittedStores.length === 0) {
          const modalRef = this.modal.open(StorageInvoicePayModal);
          modalRef.componentInstance.invoiceIds = invoiceIds;
          modalRef.componentInstance.storeIds = stores.map(r => r.id);
          modalRef.result.then(() => {
            this.loadList();
          }, () => {
            modalRef.dismiss();
          });
        } else {
          this.toastr.error('storage.invoice.paid.restriction', {storeTitles: notPermittedStores.join()});
        }
      });
    } else {
      this.toastr.error('storage.invoice.not.selected');
    }
  }

  onSupplierChanged() {
    this.navigateUrl();
  }

  onSpendChanged(spendCategory: { id: number }) {
    this.params.spendCategoryId = spendCategory.id;
    this.navigateUrl();
  }

  onQueryChanged() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      this.applyFilters();
    }, 800);
  }

  onStoreChanged(store: StoreBasicModel | StoreBasicModel[] | undefined) {
    this.brandIds = [];
    if (store && Array.isArray(store)) {
      store.forEach(r => this.brandIds.push(r.brandId));
    } else if (store && !Array.isArray(store)) {
      this.brandIds.push(store.brandId);
    }
    this.applyFilters();
  }

  private loadList() {
    this.list = [];
    if (this.params.storeIds?.length) {
      this.isLoading.list = true;
      this.storageInvoiceService.getStorageInvoiceList(this.params).subscribe(result => {
        this.list = result.list || [];
        this.pagination.allItemCount = result.count;
        this.isLoading.list = false;
      }, err => this.errorService.check(err.error));
    }
  }

  private navigateUrl() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }
}
